import NamePlateComponent from "@/components/dashboard/general/svgs/NamePlateComponent";
import ModeratorStreamDeckPanel from "@/components/dashboard/profile/ModeratorStreamDeckPanel";
import RunestonesCabinet from "@/components/dashboard/profile/RunestonesCabinet";
import ShieldTwoTagsCabinet from "@/components/dashboard/profile/ShieldTwoTagsCabinet";
import ShieldTagsCabinet from "@/components/dashboard/profile/ShiieldTagsCabinet";
import StreamDeckPanel from "@/components/dashboard/profile/StreamDeckPanel";
import { PLAYER_STATS_QUERY } from "@/graphql/queries/profileQueries";
import { PLAYER_STATS_SUBSCRIPTION } from "@/graphql/subscriptions/profileSubscriptions";
import { formatNumber } from "@/helpers/common/formatNumber";
import { useAuth } from "@/hooks/auth/useAuth";
import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery, useSubscription } from "@apollo/client";
import { useState } from "react";

const FEATURE_FLAGS_QUERY = gql`
    query FeatureFlagsForProfile {
        featureFlags {
            streamTeamChannelPointRewardEnabled
        }
    }
`;

export default function ProfilePage() {
    const [activeTab, setActiveTab] = useState(0);
    const [selectedVisual, setSelectedVisual] = useState<
        "shield1" | "shield2" | "avatar"
    >("shield1");

    const { user } = useAuth();
    const shield = user?.player?.shield;
    const shieldTwo = user?.player?.shieldTwo || null;
    const avatarUrl = user?.player?.avatarUrl || null;

    const hasShield2 = shieldTwo?.imageWithTagsUrl || shieldTwo?.imageUrl;
    const hasAvatar = !!avatarUrl;
    const shouldShowVisualTabs = hasShield2 || hasAvatar;

    const playerName = user?.player?.name || "";
    const clanBorderColor = user?.player?.clan?.borderColor?.hex || "#ffcc33";
    const clanRankColor = user?.player?.clan?.rankColor?.hex || "#ffcc33";
    const clanRankNumberColor =
        user?.player?.clan?.rankNumberColor?.hex || "#FDFABB";
    const overallMonthsSubscribed = Math.floor(
        (user?.player?.overallSubTimeInDays ?? 0) / 30,
    );
    const overallMonthsInGota = Math.floor(
        (user?.player?.gotaSubTimeInDays ?? 0) / 30,
    );

    // Initial query to load data
    const { data, loading, error } = useQuery(PLAYER_STATS_QUERY, {
        fetchPolicy: "cache-and-network",
    });

    const { data: featureFlagsData } = useQuery(FEATURE_FLAGS_QUERY);
    const isStreamTeamRewardFeatureEnabled = Boolean(
        featureFlagsData?.featureFlags?.streamTeamChannelPointRewardEnabled,
    );

    // Real-time subscription for updates - directly updates cache without refetch
    useSubscription(PLAYER_STATS_SUBSCRIPTION, {
        onData: ({ client, data: subscriptionData }) => {
            console.debug(
                "[ProfilePage] Subscription data received:",
                subscriptionData,
            );
            if (subscriptionData?.data?.playerStats) {
                try {
                    // Write the subscription data directly to the cache, bypassing normalization
                    client.cache.writeQuery({
                        query: PLAYER_STATS_QUERY,
                        data: {
                            playerStats: subscriptionData.data.playerStats,
                        },
                        overwrite: true, // Force overwrite to ignore missing fields
                    });
                    console.debug(
                        "[ProfilePage] Successfully updated cache with wheelOfHamingjaWins:",
                        subscriptionData.data.playerStats.wheelOfHamingjaWins,
                    );
                } catch (error) {
                    console.error(
                        "[ProfilePage] Error writing to cache:",
                        error,
                    );
                }
            }
        },
    });

    // Log whenever data changes
    console.debug(
        "[ProfilePage] Current wheelOfHamingjaWins:",
        data?.playerStats?.wheelOfHamingjaWins,
    );

    if (loading && !data) return <p>Loading...</p>;
    if (error && data) {
        console.warn("Error loading player stats — showing cached data.");
    }

    const renderSelectedImage = (): string | undefined => {
        if (selectedVisual === "shield2") {
            return (
                shieldTwo?.imageWithTagsUrl ?? shieldTwo?.imageUrl ?? undefined
            );
        }
        if (selectedVisual === "avatar") {
            return avatarUrl ?? undefined;
        }
        return shield!.imageWithTagsUrl ?? shield!.imageUrl ?? undefined;
    };

    const totalStats = [
        {
            name: "Shield Value",
            stat: formatNumber(data?.playerStats?.shieldValue),
        },
        ...(hasShield2
            ? [
                  {
                      name: "Shield 2 Value",
                      stat: formatNumber(data?.playerStats?.shieldTwoValue),
                  },
              ]
            : []),
        {
            name: "Lifetime VP Rank",
            stat: formatNumber(data?.playerStats?.lifetimeRank),
        },
        {
            name: "Season Rank",
            stat: formatNumber(data?.playerStats?.overallRank),
        },
        {
            name: user?.player?.clan?.tag + " Rank",
            stat: formatNumber(data?.playerStats?.clanRank),
        },
    ];

    const stats = [
        {
            title: "Lifetime VP",
            value: formatNumber(data?.playerStats?.lifetimeVp || 0),
            color: "border-brand",
        },
        {
            title: "Season VP",
            value: formatNumber(data?.playerStats?.seasonVp || 0),
            color: "border-brand",
        },
        {
            title: "Checkins",
            value: formatNumber(data?.playerStats?.checkins || 0),
            color: "border-checkins",
        },
        {
            title: "Raids",
            value: formatNumber(data?.playerStats?.raids || 0),
            color: "border-raids",
        },
        {
            title: "Sentry Time",
            value: `${formatNumber(Math.floor(data?.playerStats?.sentryTime / 60))} hours`,
            color: "border-sentry",
        },
        {
            title: "Bits",
            value: formatNumber(data?.playerStats?.bits || 0),
            color: "border-rainbow",
        },
        {
            title: "Gifted Subs",
            value: formatNumber(data?.playerStats?.giftedSubs || 0),
            color: "border-gifted",
        },
        {
            title: "Wheel of Hamingja",
            value: formatNumber(data?.playerStats?.wheelOfHamingjaWins || 0),
            color: "border-hamingja",
        },
        {
            title: "Exalted",
            value: formatNumber(data?.playerStats?.exalted || 0),
            color: "border-exalted",
        },
        {
            title: "Reforge Tokens",
            value: formatNumber(data?.playerStats?.reforgeTokens || 0),
            color: "border-exalted",
        },
        {
            title: "WOH Tokens",
            value: formatNumber(data?.playerStats?.wohTokens || 0),
            color: "border-hamingja",
        },
        ...(isStreamTeamRewardFeatureEnabled
            ? [
                  {
                      title: "Ravenguard Points",
                      value: formatNumber(
                          data?.playerStats?.ravenguardPoints || 0,
                      ),
                      color: "border-brand",
                  },
              ]
            : []),
        {
            title: "Shield Mates",
            value: formatNumber(data?.playerStats?.shieldMatesCount || 0),
            color: "border-brand",
        },
        {
            title: "Months Subscribed",
            value: formatNumber(overallMonthsSubscribed),
            color: "border-brand",
        },
        {
            title: "Total Donated",
            value:
                "$" +
                formatNumber(data?.playerStats?.totalDonated || 0) +
                " AUD",
            color: "border-brand",
        },
        {
            title: "Merchandise",
            value:
                "$" +
                formatNumber(data?.playerStats?.merchandise || 0) +
                " AUD",
            color: "border-brand",
        },
    ];

    if (overallMonthsInGota > 0) {
        stats.push({
            title: "Months in Gota",
            value: formatNumber(overallMonthsInGota),
            color: "border-brand",
        });
    }

    // Shield 2 stats (progress since Shield 2 creation)
    const shieldTwoStats = hasShield2
        ? [
              {
                  title: "Shield 2 VP",
                  value: formatNumber(data?.playerStats?.shieldTwoVp || 0),
                  color: "border-brand",
              },
              {
                  title: "Checkins",
                  value: formatNumber(
                      data?.playerStats?.shieldTwoCheckins || 0,
                  ),
                  color: "border-checkins",
              },
              {
                  title: "Raids",
                  value: formatNumber(data?.playerStats?.shieldTwoRaids || 0),
                  color: "border-raids",
              },
              {
                  title: "Sentry Time",
                  value: `${formatNumber(Math.floor((data?.playerStats?.shieldTwoSentryTime || 0) / 60))} hours`,
                  color: "border-sentry",
              },
              {
                  title: "Bits",
                  value: formatNumber(data?.playerStats?.shieldTwoBits || 0),
                  color: "border-rainbow",
              },
              {
                  title: "Gifted Subs",
                  value: formatNumber(
                      data?.playerStats?.shieldTwoGiftedSubs || 0,
                  ),
                  color: "border-gifted",
              },
              {
                  title: "Wheel of Hamingja",
                  value: formatNumber(
                      data?.playerStats?.shieldTwoWheelOfHamingjaWins || 0,
                  ),
                  color: "border-hamingja",
              },
              ...(isStreamTeamRewardFeatureEnabled
                  ? [
                        {
                            title: "Ravenguard Points",
                            value: formatNumber(
                                data?.playerStats?.shieldTwoRavenguardPoints ||
                                    0,
                            ),
                            color: "border-brand",
                        },
                    ]
                  : []),
              {
                  title: "Total Donated",
                  value:
                      "$" +
                      formatNumber(
                          data?.playerStats?.shieldTwoTotalDonated || 0,
                      ) +
                      " AUD",
                  color: "border-brand",
              },
              {
                  title: "Merchandise",
                  value:
                      "$" +
                      formatNumber(
                          data?.playerStats?.shieldTwoMerchandise || 0,
                      ) +
                      " AUD",
                  color: "border-brand",
              },
              {
                  title: "Months Subscribed",
                  value: formatNumber(
                      data?.playerStats?.shieldTwoMonthsSubscribed || 0,
                  ),
                  color: "border-brand",
              },
              {
                  title: "Months in Gota",
                  value: formatNumber(
                      data?.playerStats?.shieldTwoGotaMonths || 0,
                  ),
                  color: "border-brand",
              },
          ]
        : [];

    // Determine whether to show Shield 2 context
    const showShieldTwo = selectedVisual === "shield2" && hasShield2;
    const displayStats = showShieldTwo ? shieldTwoStats : stats;

    const tabs = [
        { title: "Shield Tags", content: <ShieldTagsCabinet />, enabled: true },
        ...(hasShield2
            ? [
                  {
                      title: "Shield 2 Tags",
                      content: <ShieldTwoTagsCabinet />,
                      enabled: true,
                  },
              ]
            : []),
        { title: "Runestones", content: <RunestonesCabinet />, enabled: true },
        { title: "Actions", content: <StreamDeckPanel />, enabled: true },
    ];

    if (user?.player?.moderator) {
        tabs.push({
            title: "Moderator Actions",
            content: <ModeratorStreamDeckPanel />,
            enabled: true,
        });
    }

    tabs.push({
        title: "Inventory",
        content: <div>Content for Inventory</div>,
        enabled: false,
    });
    tabs.push({
        title: "History",
        content: <div>Content for History</div>,
        enabled: false,
    });
    // tabs.push({
    //     title: "YGGY",
    //     content: <div>Content for YGGY</div>,
    //     enabled: false,
    // });

    // Find the Shield 2 Tags tab index for syncing visual selection
    const shieldTwoTabIndex = tabs.findIndex(
        (tab) => tab.title === "Shield 2 Tags",
    );
    const shieldOneTabIndex = tabs.findIndex(
        (tab) => tab.title === "Shield Tags",
    );

    const handleTabChange = (index: number) => {
        setActiveTab(index);
        if (index === shieldTwoTabIndex && shieldTwoTabIndex !== -1) {
            setSelectedVisual("shield2");
        } else if (index === shieldOneTabIndex) {
            setSelectedVisual("shield1");
        }
    };

    return (
        <div className="flex min-h-screen w-full flex-col items-center p-4">
            <div className="w-full">
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
                    {/* Left column */}
                    <div className="border-brand flex flex-col border-4 bg-black/70 pb-4 md:col-span-1">
                        <div className="relative">
                            <img
                                src={renderSelectedImage()}
                                alt="Profile"
                                className={`border-b-brand w-full border-b-4 object-cover ${
                                    selectedVisual !== "avatar" ? "p-4" : ""
                                }`}
                                style={{
                                    backgroundImage: `url(/wallhalla-blank-wall.png)`,
                                }}
                            />

                            <NamePlateComponent
                                text={playerName}
                                className="absolute bottom-2 left-1/2 z-10 max-w-full -translate-x-1/2 transform"
                            />
                        </div>

                        {shouldShowVisualTabs && (
                            <div className="flex justify-center space-x-2 py-2">
                                <button
                                    onClick={() => {
                                        setSelectedVisual("shield1");
                                        if (shieldOneTabIndex !== -1)
                                            setActiveTab(shieldOneTabIndex);
                                    }}
                                    className={`font-prviking rounded px-3 py-1 text-sm sm:text-base ${
                                        selectedVisual === "shield1"
                                            ? "bg-brand text-brand_charcoal"
                                            : "hover:bg-brand hover:text-brand_charcoal bg-gray-800 text-white"
                                    }`}
                                >
                                    {convertLetters("Shield 1")}
                                </button>
                                {hasShield2 && (
                                    <button
                                        onClick={() => {
                                            setSelectedVisual("shield2");
                                            if (shieldTwoTabIndex !== -1)
                                                setActiveTab(shieldTwoTabIndex);
                                        }}
                                        className={`font-prviking rounded px-3 py-1 text-sm sm:text-base ${
                                            selectedVisual === "shield2"
                                                ? "bg-brand text-brand_charcoal"
                                                : "hover:bg-brand hover:text-brand_charcoal bg-gray-800 text-white"
                                        }`}
                                    >
                                        {convertLetters("Shield 2")}
                                    </button>
                                )}
                                {hasAvatar && (
                                    <button
                                        onClick={() =>
                                            setSelectedVisual("avatar")
                                        }
                                        className={`font-prviking rounded px-3 py-1 text-sm sm:text-base ${
                                            selectedVisual === "avatar"
                                                ? "bg-brand text-brand_charcoal"
                                                : "hover:bg-brand hover:text-brand_charcoal bg-gray-800 text-white"
                                        }`}
                                    >
                                        {convertLetters("Avatar")}
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="mt-5 grid grid-cols-2 gap-2 px-2 sm:grid-cols-3 sm:px-4">
                            {displayStats.map((stat, index) => (
                                <div
                                    key={index}
                                    className={`rounded-lg border-4 bg-black/70 px-2 py-2 text-center text-xs sm:text-sm ${stat.color}`}
                                >
                                    <dt className="text-brand font-prviking wrap-break-word">
                                        {convertLetters(stat.title)}
                                    </dt>
                                    <dd className="text-stats font-garamond mt-1 text-lg wrap-break-word">
                                        {stat.value}
                                    </dd>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right column */}
                    <div className="flex w-full flex-col md:col-span-3">
                        <dl
                            className={`grid grid-cols-2 gap-4 pb-4 sm:grid-cols-2 ${hasShield2 ? "md:grid-cols-5" : "md:grid-cols-4"}`}
                        >
                            {totalStats.map((item, index) => {
                                const isLast = index === totalStats.length - 1;
                                return (
                                    <div
                                        key={item.name}
                                        className={`overflow-hidden rounded-lg border-4 bg-black/80 px-4 py-5 shadow-sm ${
                                            !isLast ? "border-brand" : ""
                                        }`}
                                        style={
                                            isLast
                                                ? {
                                                      borderColor:
                                                          clanBorderColor,
                                                  }
                                                : {}
                                        }
                                    >
                                        <dt
                                            className={`font-prviking truncate text-center text-lg sm:text-2xl ${
                                                !isLast ? "text-brand" : ""
                                            }`}
                                            style={
                                                isLast
                                                    ? { color: clanRankColor }
                                                    : {}
                                            }
                                        >
                                            {convertLetters(item.name)}
                                        </dt>
                                        <dd
                                            className={`font-garamond mt-1 text-center text-lg sm:text-2xl ${
                                                !isLast ? "text-stats" : ""
                                            }`}
                                            style={
                                                isLast
                                                    ? {
                                                          color: clanRankNumberColor,
                                                      }
                                                    : {}
                                            }
                                        >
                                            {item.stat}
                                        </dd>
                                    </div>
                                );
                            })}
                        </dl>

                        <div className="border-brand w-full grow border-4 bg-black/70">
                            <div className="mb-4 overflow-x-auto">
                                <nav className="border-b-brand flex w-full overflow-x-auto border-b-4 whitespace-nowrap">
                                    {tabs.map((tab, index) => {
                                        const isDisabled = !tab.enabled;
                                        const borderRightClass =
                                            index < tabs.length - 1
                                                ? "border-r-4 border-r-brand"
                                                : "";

                                        return (
                                            <button
                                                key={index}
                                                onClick={() =>
                                                    !isDisabled &&
                                                    handleTabChange(index)
                                                }
                                                disabled={isDisabled}
                                                className={`font-prviking min-w-25 shrink-0 px-3 py-2 text-center text-sm leading-tight wrap-break-word sm:min-w-30 sm:text-base md:min-w-0 md:flex-1 md:text-2xl ${activeTab === index ? "bg-brand text-brand_charcoal" : ""} ${!isDisabled && activeTab !== index ? "text-brand hover:bg-brand hover:text-brand_charcoal" : ""} ${isDisabled ? "cursor-not-allowed text-gray-400 opacity-50" : ""} ${borderRightClass}`}
                                            >
                                                {convertLetters(tab.title)}
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>

                            <div className="px-2 sm:px-4">
                                {tabs[activeTab].content}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
